import { useEffect, useState } from 'react'
import { Search, Filter, MapPin } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { Campaign } from '../types/database'
import CampaignCard from '../components/campaigns/CampaignCard'

interface CampaignsProps {
  user: any
}

export default function Campaigns({ user }: CampaignsProps) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTheme, setSelectedTheme] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('active')

  useEffect(() => {
    fetchCampaigns()
  }, [selectedTheme, selectedStatus])

  const fetchCampaigns = async () => {
    setLoading(true)
    let query = supabase.from('campaigns').select('*').eq('is_published', true)

    if (selectedStatus !== 'all') {
      query = query.eq('status', selectedStatus)
    }

    if (selectedTheme !== 'all') {
      query = query.eq('theme', selectedTheme)
    }

    const { data } = await query.order('current_signatures', { ascending: false })

    setCampaigns(data || [])
    setLoading(false)
  }

  const filteredCampaigns = campaigns.filter((campaign) =>
    campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    campaign.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-section text-neutral-800 mb-2">
            Descubrir Campañas
          </h1>
          <p className="text-neutral-600">
            Únete a movimientos que están generando cambio real en España
          </p>
        </div>

        {/* Barra de búsqueda y filtros */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Búsqueda */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar campañas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                />
              </div>
            </div>

            {/* Filtros */}
            <div className="flex gap-4">
              <select
                value={selectedTheme}
                onChange={(e) => setSelectedTheme(e.target.value)}
                className="px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
              >
                <option value="all">Todos los temas</option>
                <option value="Medio Ambiente">Medio Ambiente</option>
                <option value="Educación">Educación</option>
                <option value="Justicia Social">Justicia Social</option>
                <option value="Política">Política</option>
                <option value="Salud">Salud</option>
              </select>

              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
              >
                <option value="active">Activas</option>
                <option value="won">Ganadas</option>
                <option value="completed">Completadas</option>
                <option value="all">Todas</option>
              </select>
            </div>
          </div>
        </div>

        {/* Grid de campañas */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow-md h-96 animate-pulse"></div>
            ))}
          </div>
        ) : filteredCampaigns.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCampaigns.map((campaign) => (
              <CampaignCard key={campaign.id} campaign={campaign} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Filter className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <p className="text-neutral-600">No se encontraron campañas con estos filtros</p>
          </div>
        )}
      </div>
    </div>
  )
}
